import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Navigate } from 'react-router-dom';
import type { editor as MonacoEditor } from 'monaco-editor';
import { useAuth } from '../../contexts/AuthContext';
import { useProject, SaveStatus } from '../../hooks/useProject';
import { useLatexCompiler } from '../../hooks/useLatexCompiler';
import { useAIAssistance } from '../../hooks/useAIAssistance';
import { useComments } from '../../hooks/useComments';
import { useProjectChats } from '../../hooks/useProjectChats';
import { useVerifierRunner } from '../../hooks/useVerifierRunner';
import { useOTSync } from '../../hooks/useOTSync';
import { useUserFlags } from '../../hooks/useUserFlags';
import { usePresence, getInitials } from '../../hooks/usePresence';
import { LatexEditor } from '../../components/Editor/LatexEditor';
import { ImagePreview } from '../../components/Editor/ImagePreview';
import { LargeFilePlaceholder } from '../../components/Editor/LargeFilePlaceholder';
import { isStorageBackedFile } from '../../services/projects';
import { PDFViewer } from '../../components/PDFPreview/PDFViewer';
import { VerifierOutputPanel } from '../../components/Verifier/VerifierOutputPanel';
import { ChatPanel } from '../../components/Chat/ChatPanel';
import { FileTree } from '../../components/Sidebar/FileTree';
import { ChatList } from '../../components/Sidebar/ChatList';
import { FloatingChatInput } from '../../components/Chat/FloatingChatInput';
import { DiffReviewPanel } from '../../components/DiffReview';
import { SelectionPrompt } from '../../components/Editor/SelectionPrompt';
import { CommentPrompt } from '../../components/Comments/CommentPrompt';
import { CommentsPanel } from '../../components/Comments/CommentsPanel';
import { ShareDialog } from '../../components/Share/ShareDialog';
import { RevisionList } from '../../components/RevisionHistory/RevisionList';
import { RevisionPreview } from '../../components/RevisionHistory/RevisionPreview';
import { useVersionReloadGuard } from '../../components/VersionWatcher';
import { getRevisionFiles, getRevisionPdfData } from '../../services/revisions';
import {
  restoreFiles,
  updateProject,
} from '../../services/projects';
import { createProjectChat, deleteProjectChat, updateProjectChat } from '../../services/chats';
import { addComment, getCommentActionErrorMessage } from '../../services/comments';
import { getUserProjectRole } from '../../services/sharing';
import { createReviewRequest, getProjectReviewRequests } from '../../services/reviews';
import { requestChatAssistance, requestProjectChatAssistance } from '../../services/ai';
import {
  AIAssistConversationMessage,
  ProjectFile,
  ProjectChatMessage,
  Revision,
  ReviewRequest,
} from '../../types';
import { OTComponents } from '../../types/ot';
import { downloadProjectZip } from '../../utils/projectZip';
import { getAIValidationTargetFile, getCompileTargetFile } from './compileTargets';
import { buildProjectZipAdditionalEntries } from './projectArchive';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ensureAdminProjectCommentAccess } from '../../services/admin';
import {
  FileText,
  ArrowLeft,
  FolderTree,
  Loader2,
  Monitor,
  Play,
  Share2,
  Check,
  Save,
  WifiOff,
  History,
  Send,
  MessageSquare,
  LogIn,
} from 'lucide-react';

const EMPTY_CHAT_TITLE = 'New chat';
const VERSION_RELOAD_IDLE_MS = 10000;
const PDF_PANEL_DEFAULT_RATIO = 0.38;
const PDF_PANEL_MIN_WIDTH = 280;
const MAIN_PANEL_MIN_WIDTH = 360;
const PANEL_RESIZER_WIDTH = 10;
const MISSING_MAIN_TEX_ERROR = [
  'Missing main.tex',
  'This project does not have a main.tex file to compile.',
  'Rename your entry file to main.tex or add one that includes the rest of the document.',
].join('\n');

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function createChatMessage(role: ProjectChatMessage['role'], content: string): ProjectChatMessage {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    role,
    content,
    createdAt: new Date(),
  };
}

function buildChatTitle(messages: ProjectChatMessage[]): string {
  const firstUserMessage = messages.find((message) => message.role === 'user')?.content
    || messages[0]?.content
    || EMPTY_CHAT_TITLE;
  const normalized = firstUserMessage.replace(/\s+/g, ' ').trim();
  return normalized.length > 48 ? `${normalized.slice(0, 45)}...` : normalized;
}

function isPlaceholderChatTitle(title?: string | null): boolean {
  return !title || title === EMPTY_CHAT_TITLE || title === 'Untitled chat';
}

function isOpenReviewStatus(status: ReviewRequest['status']): boolean {
  return status === 'waiting_for_review' || status === 'in_review';
}

function parseChatPrompt(rawPrompt: string): {
  prompt: string;
  includeProjectContext: boolean;
} {
  const trimmed = rawPrompt.trim();
  const contextPrefix = /^\/(?:context|project)\b/i;

  if (!contextPrefix.test(trimmed)) {
    return {
      prompt: trimmed,
      includeProjectContext: false,
    };
  }

  return {
    prompt: trimmed.replace(contextPrefix, '').trim(),
    includeProjectContext: true,
  };
}

function resolveChatTitle(
  existingTitle: string | null | undefined,
  messages: ProjectChatMessage[]
): string {
  if (messages.length > 0 && isPlaceholderChatTitle(existingTitle)) {
    return buildChatTitle(messages);
  }

  return existingTitle || buildChatTitle(messages);
}

function toConversationHistory(messages: ProjectChatMessage[]): AIAssistConversationMessage[] {
  return messages.map((message) => ({
    role: message.role,
    content: message.content,
  }));
}

export function EditorPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, loading: authLoading } = useAuth();
  const userFlags = useUserFlags(currentUser && !currentUser.isAnonymous ? currentUser.uid : null);
  const canRunVerifierAcrossProjects = userFlags.isReviewer || userFlags.isAdmin || userFlags.isOwner;
  const isAdmin = userFlags.isAdmin;
  const isOwner = userFlags.isOwner;
  const isAnonymous = currentUser?.isAnonymous === true;
  const isMobile = useIsMobile();
  const {
    project,
    files,
    activeFile,
    loading,
    error,
    updateActiveFileContent,
    setFiles,
    refreshFiles,
    flushSave,
    saveFile,
    switchToFile,
    saveStatus,
    lastSavedAt,
    isOTEnabled,
  } = useProject(projectId);
  const {
    compile,
    validateCompile,
    compiling,
    result,
    engineReady,
    revisionLoading,
    replaceResult,
  } = useLatexCompiler(projectId);
  const verifier = useVerifierRunner({
    projectId,
    files,
  });

  // OT: accumulate remote operations for the editor to apply
  const [pendingRemoteOps, setPendingRemoteOps] = useState<Array<{ op: OTComponents; userId: string }>>([]);

  // Track which fileId we've captured initial content for.
  // Only updates when activeFile.id changes — prevents engine re-creation on keystrokes.
  const [otFileId, setOtFileId] = useState<string | undefined>(undefined);
  const [otInitialContent, setOtInitialContent] = useState('');
  const [otInitialVersion, setOtInitialVersion] = useState(0);
  useEffect(() => {
    if (activeFile && activeFile.id !== otFileId) {
      setOtFileId(activeFile.id);
      setOtInitialContent(activeFile.content);
      setOtInitialVersion(activeFile.version ?? 0);
    }
  }, [activeFile, otFileId]);

  // OT sync hook — initialContent/Version only change when file switches
  const otSync = useOTSync({
    projectId,
    fileId: otFileId,
    userId: currentUser?.uid,
    initialContent: otInitialContent,
    initialVersion: otInitialVersion,
    enabled: isOTEnabled,
    onRemoteOp: useCallback((op: OTComponents, userId: string) => {
      setPendingRemoteOps(prev => [...prev, { op, userId }]);
    }, []),
  });

  // Log OT state for debugging (set OT_DEBUG in useOTSync.ts to enable)
  // useEffect(() => {
  //   console.log(`[OT] EditorPage: isOTEnabled=${isOTEnabled}, otFileId=${otFileId}, syncStatus=${otSync.syncStatus}, engineState=${otSync.engineState}`);
  // }, [isOTEnabled, otFileId, otSync.syncStatus, otSync.engineState]);

  // Keep the files array's version in sync with the OT engine's version.
  // This prevents stale-version bugs when switching away and back to a file:
  // without this, the re-created engine would subscribe from version 0 and
  // replay all historical ops as remote ops, duplicating content.
  useEffect(() => {
    if (!isOTEnabled || !otFileId) return;
    setFiles(prev => prev.map(f =>
      f.id === otFileId ? { ...f, version: otSync.currentVersion } : f
    ));
  }, [isOTEnabled, otFileId, otSync.currentVersion, setFiles]);

  // Clear pending remote ops after they've been applied by the editor
  useEffect(() => {
    if (pendingRemoteOps.length > 0) {
      const timer = setTimeout(() => setPendingRemoteOps([]), 0);
      return () => clearTimeout(timer);
    }
  }, [pendingRemoteOps]);

  // Called by LatexEditor after applying remote ops via pushEditOperations.
  // This syncs activeFile.content for compilation without causing Monaco to reset.
  const handleRemoteContentSync = useCallback((content: string) => {
    updateActiveFileContent(content);
  }, [updateActiveFileContent]);

  // Presence hook — enabled for authenticated (non-anonymous) users
  const presence = usePresence({
    projectId,
    userId: currentUser?.uid,
    displayName: currentUser?.displayName || currentUser?.email || null,
    activeFileId: activeFile?.id || null,
    enabled: !isAnonymous,
  });

  // Remote users editing the same file
  const remoteUsersInFile = presence.remoteUsers.filter(
    u => u.activeFileId === activeFile?.id
  );

  const spellcheckEnabled = true;
  const [showPresencePopup, setShowPresencePopup] = useState(false);
  const presencePopupRef = useRef<HTMLDivElement>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [previewRevision, setPreviewRevision] = useState<Revision | null>(null);
  const [showComments, setShowComments] = useState(false);
  const [sidebarTab, setSidebarTab] = useState<'files' | 'chats'>('files');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showReviewSubmitModal, setShowReviewSubmitModal] = useState(false);
  const [showReviewSubmittedModal, setShowReviewSubmittedModal] = useState(false);
  const [reviewSubmitNotes, setReviewSubmitNotes] = useState('');
  const [reviewSubmitError, setReviewSubmitError] = useState<string | null>(null);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [accessRole, setAccessRole] = useState<'owner' | 'read' | 'comment' | 'edit' | null>(null);
  const [autoCompiled, setAutoCompiled] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [restoreNotice, setRestoreNotice] = useState<string | null>(null);
  const [reviewEnabled, setReviewEnabled] = useState(false);
  const [latestOpenReview, setLatestOpenReview] = useState<ReviewRequest | null>(null);
  const [latestReview, setLatestReview] = useState<ReviewRequest | null>(null);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [chatMessages, setChatMessages] = useState<ProjectChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);
  const [projectTitle, setProjectTitle] = useState('');
  const [projectTitleDraft, setProjectTitleDraft] = useState('');
  const [editingProjectTitle, setEditingProjectTitle] = useState(false);
  const [savingProjectTitle, setSavingProjectTitle] = useState(false);
  const [projectTitleError, setProjectTitleError] = useState<string | null>(null);
  const [pdfPanelWidth, setPdfPanelWidth] = useState<number | null>(null);
  const [isResizingPdfPanel, setIsResizingPdfPanel] = useState(false);
  const [chatDrafts, setChatDrafts] = useState<Record<string, string>>({});

  // Selection state for AI
  const [selectionPromptOpen, setSelectionPromptOpen] = useState(false);
  const [currentSelection, setCurrentSelection] = useState<{
    startLine: number;
    endLine: number;
    text: string;
  } | null>(null);

  // Comment selection
  const [commentPromptOpen, setCommentPromptOpen] = useState(false);
  const [commentSubmitError, setCommentSubmitError] = useState<string | null>(null);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentSelection, setCommentSelection] = useState<{
    startLine: number;
    endLine: number;
    startColumn: number;
    endColumn: number;
    text: string;
  } | null>(null);

  const restoreNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chatCreationInFlightRef = useRef(false);
  const pendingChatActivationRef = useRef<string | null>(null);
  const lastEditorInputAtRef = useRef(Date.now());
  const projectTitleInputRef = useRef<HTMLInputElement>(null);
  const editingProjectTitleRef = useRef(false);
  const editorWorkspaceRef = useRef<HTMLDivElement>(null);
  const editorViewStatesRef = useRef<Record<string, MonacoEditor.ICodeEditorViewState | null>>({});
  const chatScrollPositionsRef = useRef<Record<string, number>>({});
  const backTarget = location.state && typeof location.state === 'object' && 'backTarget' in location.state
    ? location.state.backTarget
    : null;
  const fallbackBackPath = isAnonymous ? '/' : '/projects';
  const fallbackBackLabel = isAnonymous ? 'Back to home' : 'Back to projects';

  const handleBackNavigation = useCallback(() => {
    if (
      backTarget
      && typeof backTarget === 'object'
      && typeof backTarget.pathname === 'string'
      && backTarget.pathname
    ) {
      navigate(backTarget.pathname, { state: backTarget.state });
      return;
    }

    navigate(fallbackBackPath);
  }, [backTarget, fallbackBackPath, navigate]);

  const backButtonTitle = (
    backTarget
    && typeof backTarget === 'object'
    && typeof backTarget.pathname === 'string'
    && backTarget.pathname === '/admin'
  )
    ? 'Back to admin'
    : fallbackBackLabel;

  const formatRevisionTimestamp = useCallback((date: Date) => (
    date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  ), []);

  // Close presence popup on outside click
  useEffect(() => {
    if (!showPresencePopup) return;
    const handleClick = (e: MouseEvent) => {
      if (presencePopupRef.current && !presencePopupRef.current.contains(e.target as Node)) {
        setShowPresencePopup(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showPresencePopup]);

  // AI assistance hook
  const validateAIChanges = useCallback(async (newContent: string) => {
    if (!activeFile) {
      return { ok: false, error: 'No active file selected.' };
    }

    const updatedFiles = files.map((file) => (
      file.id === activeFile.id ? { ...file, content: newContent } : file
    ));
    const compileTargetFile = getAIValidationTargetFile(updatedFiles);

    if (!compileTargetFile) {
      return { ok: true };
    }

    const validation = await validateCompile(compileTargetFile.content, updatedFiles);
    return validation.pdf
      ? { ok: true }
      : { ok: false, error: validation.log };
  }, [activeFile, files, validateCompile]);

  const aiAssistance = useAIAssistance({
    projectId: projectId || '',
    activeFile,
    allFiles: files,
    onApplyChanges: (newContent) => {
      if (activeFile) {
        updateActiveFileContent(newContent);
      }
    },
    validateChanges: validateAIChanges,
  });

  const { comments, loading: commentsLoading } = useComments(
    projectId || null,
    activeFile?.id || null
  );
  const { chats, loading: chatsLoading } = useProjectChats(projectId || null);
  const activeChat = chats.find((chat) => chat.id === activeChatId) || null;

  const canEdit = !isAnonymous && (accessRole === 'owner' || accessRole === 'edit');
  const canRunVerifier = accessRole !== null || canRunVerifierAcrossProjects;
  const canComment = !isAnonymous && (canEdit || accessRole === 'comment');
  const canCompile = accessRole !== null;
  const loadedProjectId = project?.id;
  const loadedProjectTitle = project?.title;
  const effectiveProjectTitle = projectTitle || project?.title || 'Untitled Project';
  const isVerifierFile = activeFile
    ? (activeFile.name.toLowerCase().endsWith('.py') || activeFile.name.toLowerCase().endsWith('.lean'))
    : false;
  const showVerifierPanel = canRunVerifier && (verifier.running || isVerifierFile);
  const activeFileCompileDiagnostics = activeFile
    ? (result?.diagnostics || []).filter((diagnostic) => (
      !diagnostic.file || diagnostic.file === activeFile.name
    ))
    : [];
  const activeConversationKey = activeChatId || 'draft';
  const activeChatDraft = chatDrafts[activeConversationKey] || '';
  const activeChatScrollTop = chatScrollPositionsRef.current[activeConversationKey] ?? null;

  const clampPdfWidth = useCallback((desiredWidth: number, workspaceWidth: number) => {
    if (workspaceWidth <= 0) {
      return desiredWidth;
    }

    const maxWidth = Math.max(
      PDF_PANEL_MIN_WIDTH,
      workspaceWidth - MAIN_PANEL_MIN_WIDTH - PANEL_RESIZER_WIDTH
    );
    const minWidth = Math.min(PDF_PANEL_MIN_WIDTH, maxWidth);
    return clamp(desiredWidth, minWidth, maxWidth);
  }, []);

  const handleEditorViewStateChange = useCallback((
    nextFileId: string,
    viewState: MonacoEditor.ICodeEditorViewState | null
  ) => {
    editorViewStatesRef.current[nextFileId] = viewState;
  }, []);

  const handleChatDraftChange = useCallback((value: string) => {
    setChatDrafts((prev) => (
      prev[activeConversationKey] === value
        ? prev
        : { ...prev, [activeConversationKey]: value }
    ));
  }, [activeConversationKey]);

  const handleChatScrollPositionChange = useCallback((scrollTop: number) => {
    chatScrollPositionsRef.current[activeConversationKey] = scrollTop;
  }, [activeConversationKey]);

  // OT-aware save tracking: derive save status from OT engine state
  const [otSavePhase, setOtSavePhase] = useState<SaveStatus>('idle');
  const [otSavedAt, setOtSavedAt] = useState<Date | null>(null);
  const prevEngineStateRef = useRef(otSync.engineState);
  const otSavedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOTEnabled) return;
    const prev = prevEngineStateRef.current;
    const curr = otSync.engineState;
    prevEngineStateRef.current = curr;

    if (curr !== 'synchronized') {
      setOtSavePhase('saving');
      if (otSavedTimerRef.current) {
        clearTimeout(otSavedTimerRef.current);
        otSavedTimerRef.current = null;
      }
    } else if (prev !== 'synchronized') {
      setOtSavePhase('saved');
      setOtSavedAt(new Date());
      otSavedTimerRef.current = setTimeout(() => setOtSavePhase('idle'), 2000);
    }
  }, [isOTEnabled, otSync.engineState]);

  useEffect(() => {
    return () => {
      if (otSavedTimerRef.current) clearTimeout(otSavedTimerRef.current);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (restoreNoticeTimerRef.current) clearTimeout(restoreNoticeTimerRef.current);
    };
  }, []);

  useEffect(() => {
    editingProjectTitleRef.current = editingProjectTitle;
  }, [editingProjectTitle]);

  useEffect(() => {
    if (!loadedProjectId) return;

    const nextTitle = loadedProjectTitle || 'Untitled Project';
    setProjectTitle(nextTitle);
    if (!editingProjectTitleRef.current) {
      setProjectTitleDraft(nextTitle);
      setProjectTitleError(null);
    }
  }, [loadedProjectId, loadedProjectTitle]);

  useEffect(() => {
    if (!editingProjectTitle) return;

    const frame = window.requestAnimationFrame(() => {
      projectTitleInputRef.current?.focus();
      projectTitleInputRef.current?.select();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [editingProjectTitle]);

  useEffect(() => {
    const workspace = editorWorkspaceRef.current;
    if (!workspace) return;

    const updateWidth = () => {
      const workspaceWidth = workspace.clientWidth;
      setPdfPanelWidth((currentWidth) => {
        const nextWidth = clampPdfWidth(
          currentWidth ?? (workspaceWidth * PDF_PANEL_DEFAULT_RATIO),
          workspaceWidth
        );
        return currentWidth === nextWidth ? currentWidth : nextWidth;
      });
    };

    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(workspace);

    return () => observer.disconnect();
  }, [clampPdfWidth]);

  useEffect(() => {
    if (!isResizingPdfPanel) return;

    const handleMouseMove = (event: MouseEvent) => {
      const workspace = editorWorkspaceRef.current;
      if (!workspace) return;

      const rect = workspace.getBoundingClientRect();
      const desiredWidth = rect.right - event.clientX;
      setPdfPanelWidth(clampPdfWidth(desiredWidth, rect.width));
    };

    const stopResizing = () => {
      setIsResizingPdfPanel(false);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', stopResizing);

    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [clampPdfWidth, isResizingPdfPanel]);

  // Unified save status and timestamp across OT and legacy modes
  const effectiveSaveStatus: SaveStatus = isOTEnabled ? otSavePhase : saveStatus;
  const effectiveLastSavedAt = isOTEnabled ? otSavedAt : lastSavedAt;

  useVersionReloadGuard(useCallback(async () => {
    if (!canEdit) {
      return true;
    }

    if (effectiveSaveStatus === 'saving' || effectiveSaveStatus === 'error') {
      return false;
    }

    if (Date.now() - lastEditorInputAtRef.current < VERSION_RELOAD_IDLE_MS) {
      return false;
    }

    if (isOTEnabled) {
      return true;
    }

    return flushSave();
  }, [canEdit, effectiveSaveStatus, flushSave, isOTEnabled]));

  useEffect(() => {
    if (!projectId || !currentUser) return;
    let cancelled = false;

    const loadAccessRole = async () => {
      let adminCommentAccessEnsured = false;

      if (isAdmin) {
        try {
          await ensureAdminProjectCommentAccess(projectId);
          adminCommentAccessEnsured = true;
        } catch (err) {
          console.error('Failed to ensure admin comment access:', err);
        }
      }

      try {
        const role = await getUserProjectRole(projectId, currentUser.uid, isAdmin, isOwner);
        if (cancelled) return;
        setAccessRole(adminCommentAccessEnsured && role === 'read' ? 'comment' : role);
      } catch {
        if (!cancelled) {
          setAccessRole(null);
        }
      }
    };

    void loadAccessRole();

    return () => {
      cancelled = true;
    };
  }, [projectId, currentUser, isAdmin, isOwner]);

  useEffect(() => {
    setReviewEnabled(project?.reviewEnabled === true);
  }, [project?.reviewEnabled]);

  useEffect(() => {
    if (!projectId || !currentUser) return;
    let cancelled = false;

    getProjectReviewRequests(projectId)
      .then((reviews) => {
        if (cancelled) return;
        setLatestReview(reviews[0] || null);
        setLatestOpenReview(reviews.find((review) => isOpenReviewStatus(review.status)) || null);
      })
      .catch((err) => {
        console.error('Failed to load review request:', err);
        if (!cancelled) {
          setLatestReview(null);
          setLatestOpenReview(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, currentUser]);

  useEffect(() => {
    if (!activeFile || !canCompile || compiling || autoCompiled || revisionLoading) return;
    if (result?.pdf) return;
    const texFile = getCompileTargetFile(files, activeFile);
    if (!texFile) return;
    compile(texFile.content, files, { writeArtifacts: canEdit })
      .catch(() => {})
      .finally(() => setAutoCompiled(true));
  }, [activeFile, canCompile, canEdit, compiling, autoCompiled, revisionLoading, result, files, compile]);

  useEffect(() => {
    if (!activeChatId) return;
    if (!activeChat) {
      if (pendingChatActivationRef.current === activeChatId) {
        return;
      }
      setActiveChatId(null);
      setChatMessages([]);
      return;
    }

    if (pendingChatActivationRef.current === activeChat.id) {
      pendingChatActivationRef.current = null;
    }
    setChatMessages(activeChat.messages);
  }, [activeChatId, activeChat]);

  const ensureSavedChat = useCallback(async (
    messagesToSave: ProjectChatMessage[],
    fallbackTitle?: string | null
  ) => {
    if (
      !canEdit
      || !projectId
      || activeChatId
      || messagesToSave.length === 0
      || chats.length > 0
      || chatCreationInFlightRef.current
    ) {
      return activeChatId;
    }

    chatCreationInFlightRef.current = true;
    try {
      const chatId = await createProjectChat(
        projectId,
        fallbackTitle || buildChatTitle(messagesToSave),
        messagesToSave
      );
      pendingChatActivationRef.current = chatId;
      setActiveChatId(chatId);
      return chatId;
    } finally {
      chatCreationInFlightRef.current = false;
    }
  }, [activeChatId, canEdit, chats.length, projectId]);

  useEffect(() => {
    if (chatsLoading || chats.length > 0 || activeChatId || chatMessages.length === 0) {
      return;
    }

    ensureSavedChat(chatMessages).catch((err) => {
      console.error('Failed to auto-save current chat:', err);
    });
  }, [activeChatId, chatMessages, chats.length, chatsLoading, ensureSavedChat]);

  const persistCurrentChat = useCallback(async () => {
    if (!canEdit || !projectId || chatMessages.length === 0) {
      return activeChatId;
    }

    if (activeChatId) {
      await updateProjectChat(projectId, activeChatId, {
        title: resolveChatTitle(activeChat?.title, chatMessages),
        messages: chatMessages,
      });
      return activeChatId;
    }

    return createProjectChat(projectId, buildChatTitle(chatMessages), chatMessages);
  }, [canEdit, projectId, chatMessages, activeChatId, activeChat]);

  const handleNewChat = useCallback(async () => {
    try {
      await persistCurrentChat();

      if (!canEdit || !projectId) {
        setActiveChatId(null);
        setChatMessages([]);
        setChatError(null);
        return;
      }

      const newChatId = await createProjectChat(projectId, EMPTY_CHAT_TITLE, []);
      pendingChatActivationRef.current = newChatId;
      setActiveChatId(newChatId);
      setChatMessages([]);
      setSidebarTab('chats');
      setChatError(null);
    } catch (err) {
      console.error('Failed to save chat:', err);
    }
  }, [canEdit, persistCurrentChat, projectId]);

  const handleSelectChat = useCallback(async (chatId: string) => {
    if (chatId === activeChatId) {
      setSidebarTab('chats');
      return;
    }

    try {
      await persistCurrentChat();
      const nextChat = chats.find((chat) => chat.id === chatId);
      if (!nextChat) return;

      setActiveChatId(chatId);
      setChatMessages(nextChat.messages);
      setSidebarTab('chats');
      setChatError(null);
    } catch (err) {
      console.error('Failed to switch chats:', err);
    }
  }, [activeChatId, chats, persistCurrentChat]);

  const handleDeleteChat = useCallback(async (chatId: string) => {
    if (!canEdit || !projectId || deletingChatId === chatId) {
      return;
    }

    setDeletingChatId(chatId);
    try {
      await deleteProjectChat(projectId, chatId);

      if (activeChatId === chatId) {
        pendingChatActivationRef.current = null;
        setActiveChatId(null);
        setChatMessages([]);
        setChatError(null);
      }
    } catch (err) {
      console.error('Failed to delete chat:', err);
    } finally {
      setDeletingChatId((currentId) => (currentId === chatId ? null : currentId));
    }
  }, [activeChatId, canEdit, deletingChatId, projectId]);

  const handleChatSubmit = useCallback(async (prompt: string) => {
    if (!activeFile || activeFile.type === 'image') {
      return;
    }

    const { prompt: normalizedPrompt, includeProjectContext } = parseChatPrompt(prompt);
    if (!normalizedPrompt) {
      setChatError('Add a question after /context.');
      return;
    }

    const priorConversation = toConversationHistory(chatMessages);
    const userMessage = createChatMessage('user', normalizedPrompt);
    const nextMessages = [...chatMessages, userMessage];
    const nextTitle = resolveChatTitle(activeChat?.title, nextMessages);
    let chatId = activeChatId;

    setChatMessages(nextMessages);
    setChatError(null);
    setChatLoading(true);

    try {
      if (projectId && chatId) {
        await updateProjectChat(projectId, chatId, {
          title: nextTitle,
          messages: nextMessages,
        });
      } else {
        chatId = await ensureSavedChat(nextMessages, nextTitle);
      }

      const response = includeProjectContext
        ? await requestProjectChatAssistance(
          projectId || '',
          normalizedPrompt,
          activeFile,
          files,
          priorConversation
        )
        : await requestChatAssistance(normalizedPrompt, priorConversation);

      if (!response) {
        return;
      }

      const assistantMessage = createChatMessage('assistant', response);
      const completedMessages = [...nextMessages, assistantMessage];
      setChatMessages(completedMessages);

      if (projectId && chatId) {
        await updateProjectChat(projectId, chatId, {
          title: nextTitle,
          messages: completedMessages,
        });
      }
    } catch (err) {
      console.error('Failed to submit chat prompt:', err);
      setChatError(err instanceof Error ? err.message : 'Failed to submit chat prompt.');
    } finally {
      setChatLoading(false);
    }
  }, [activeChat?.title, activeChatId, activeFile, chatMessages, ensureSavedChat, files, projectId]);

  // Handle chat submit
  const handleAISubmit = useCallback((prompt: string) => {
    aiAssistance.requestAI(prompt);
  }, [aiAssistance]);

  // Handle selection-based AI
  const handleSelectionSubmit = useCallback((prompt: string) => {
    if (currentSelection) {
      aiAssistance.requestAI(prompt, currentSelection);
    }
    setSelectionPromptOpen(false);
    setCurrentSelection(null);
  }, [aiAssistance, currentSelection]);

  // Keyboard shortcut for selection prompt (Cmd+Shift+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'k') {
        e.preventDefault();
        // Get selection from window
        const selection = window.getSelection()?.toString();
        if (selection) {
          setCurrentSelection({
            startLine: 1,
            endLine: 1,
            text: selection
          });
          setSelectionPromptOpen(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Warn user if they try to close the tab while a save is in-flight
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (effectiveSaveStatus === 'saving') {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [effectiveSaveStatus]);

  const handleContentChange = useCallback(
    (content: string) => {
      if (!canEdit) return;
      lastEditorInputAtRef.current = Date.now();
      updateActiveFileContent(content);
    },
    [updateActiveFileContent, canEdit]
  );

  // OT: handle local edit operations from Monaco
  const handleOTChange = useCallback(
    (op: OTComponents) => {
      if (!canEdit || !isOTEnabled) return;
      lastEditorInputAtRef.current = Date.now();
      otSync.applyLocal(op);
    },
    [canEdit, isOTEnabled, otSync]
  );

  const handleSave = useCallback(async () => {
    if (!canEdit || !activeFile) return;
    if (isOTEnabled) {
      setOtSavePhase('saving');
      try {
        await saveFile(activeFile.id, activeFile.content);
        setOtSavePhase('saved');
        setOtSavedAt(new Date());
        if (otSavedTimerRef.current) clearTimeout(otSavedTimerRef.current);
        otSavedTimerRef.current = setTimeout(() => setOtSavePhase('idle'), 2000);
      } catch {
        setOtSavePhase('error');
      }
    } else {
      await flushSave();
    }
  }, [canEdit, activeFile, isOTEnabled, saveFile, flushSave]);

  const showMissingMainTexError = useCallback(() => {
    replaceResult({
      pdf: null,
      log: MISSING_MAIN_TEX_ERROR,
      errors: [MISSING_MAIN_TEX_ERROR],
      diagnostics: [],
    });
  }, [replaceResult]);

  const handleCompile = useCallback(async () => {
    if (!activeFile || !canCompile) return;
    if (canEdit) await flushSave();
    const texFile = getCompileTargetFile(files, activeFile);
    if (!texFile) {
      showMissingMainTexError();
      return;
    }
    await compile(texFile.content, files, { writeArtifacts: canEdit });
  }, [activeFile, compile, flushSave, files, canEdit, canCompile, showMissingMainTexError]);

  const handleRunVerifier = useCallback(async (model?: string) => {
    await verifier.run(activeFile, canRunVerifier, model);
  }, [activeFile, canRunVerifier, verifier]);

  const handlePdfPanelResizeStart = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsResizingPdfPanel(true);
  }, []);

  const handlePdfPanelResizeReset = useCallback(() => {
    const workspace = editorWorkspaceRef.current;
    if (!workspace) return;

    setPdfPanelWidth(clampPdfWidth(
      workspace.clientWidth * PDF_PANEL_DEFAULT_RATIO,
      workspace.clientWidth
    ));
  }, [clampPdfWidth]);

  useEffect(() => {
    const handleGlobalSaveShortcut = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 's') {
        return;
      }

      const target = event.target instanceof HTMLElement ? event.target : null;
      const tagName = target?.tagName.toLowerCase();
      const isNativeTextInput = !!target && (
        target.isContentEditable
        || tagName === 'input'
        || tagName === 'textarea'
        || tagName === 'select'
      );

      if (isNativeTextInput && !target?.closest('.monaco-editor')) {
        return;
      }

      event.preventDefault();

      if (isVerifierFile) {
        void handleRunVerifier();
        return;
      }

      void handleCompile();
    };

    document.addEventListener('keydown', handleGlobalSaveShortcut);
    return () => document.removeEventListener('keydown', handleGlobalSaveShortcut);
  }, [handleCompile, handleRunVerifier, isVerifierFile]);

  const handleDownload = useCallback(() => {
    if (!result?.pdf || result.pdf.byteLength === 0) return;
    const blob = new Blob([result.pdf], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${effectiveProjectTitle || 'document'}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [effectiveProjectTitle, result]);

  const handleDownloadProjectZip = useCallback(async (
    archiveTitle: string,
    projectFiles: ProjectFile[]
  ) => {
    const additionalEntries = await buildProjectZipAdditionalEntries({
      activeFile,
      canCompile,
      canEdit,
      files: projectFiles,
      flushSave,
      validateCompile,
    });

    await downloadProjectZip(archiveTitle, projectFiles, { additionalEntries });
  }, [activeFile, canCompile, canEdit, flushSave, validateCompile]);

  const cancelProjectTitleEdit = useCallback(() => {
    setEditingProjectTitle(false);
    setProjectTitleDraft(effectiveProjectTitle);
    setProjectTitleError(null);
  }, [effectiveProjectTitle]);

  const startProjectTitleEdit = useCallback(() => {
    if (!canEdit || savingProjectTitle) return;

    setProjectTitleDraft(effectiveProjectTitle);
    setProjectTitleError(null);
    setEditingProjectTitle(true);
  }, [canEdit, effectiveProjectTitle, savingProjectTitle]);

  const saveProjectTitle = useCallback(async () => {
    if (!projectId || !canEdit || savingProjectTitle) return;

    const trimmedTitle = projectTitleDraft.trim();
    if (!trimmedTitle) {
      setProjectTitleError('Project name cannot be empty.');
      return;
    }

    if (trimmedTitle === effectiveProjectTitle) {
      setEditingProjectTitle(false);
      setProjectTitleDraft(trimmedTitle);
      setProjectTitleError(null);
      return;
    }

    setSavingProjectTitle(true);
    setProjectTitleError(null);
    try {
      await updateProject(projectId, { title: trimmedTitle });
      setProjectTitle(trimmedTitle);
      setProjectTitleDraft(trimmedTitle);
      setEditingProjectTitle(false);
    } catch (err) {
      console.error('Failed to rename project:', err);
      setProjectTitleError('Failed to rename project.');
    } finally {
      setSavingProjectTitle(false);
    }
  }, [canEdit, effectiveProjectTitle, projectId, projectTitleDraft, savingProjectTitle]);

  const handleProjectTitleInputKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void saveProjectTitle();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      cancelProjectTitleEdit();
    }
  }, [cancelProjectTitleEdit, saveProjectTitle]);

  function renderProjectTitleControl(options?: { compact?: boolean }) {
    const compact = options?.compact === true;
    const textSizeClass = compact ? 'text-sm' : 'text-base';
    const maxWidthClass = compact ? 'max-w-[11rem]' : 'max-w-[18rem]';

    if (editingProjectTitle) {
      return (
        <div className={`flex min-w-0 items-center gap-2 ${maxWidthClass}`}>
          <input
            ref={projectTitleInputRef}
            type="text"
            value={projectTitleDraft}
            onChange={(event) => {
              setProjectTitleDraft(event.target.value);
              if (projectTitleError) {
                setProjectTitleError(null);
              }
            }}
            onBlur={() => { void saveProjectTitle(); }}
            onKeyDown={handleProjectTitleInputKeyDown}
            disabled={savingProjectTitle}
            aria-label="Project name"
            aria-invalid={projectTitleError ? 'true' : undefined}
            title={projectTitleError || undefined}
            className={`w-full rounded-md border px-2 py-1 font-medium text-gray-900 outline-none transition ${textSizeClass} ${
              projectTitleError
                ? 'border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100'
                : 'border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary-light'
            } ${savingProjectTitle ? 'bg-gray-50 text-gray-500' : 'bg-white'}`}
          />
          {savingProjectTitle && (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" />
          )}
        </div>
      );
    }

    if (canEdit) {
      return (
        <button
          type="button"
          onClick={startProjectTitleEdit}
          title="Rename project"
          className={`min-w-0 truncate text-left font-medium text-gray-900 transition hover:text-primary ${textSizeClass} ${maxWidthClass}`}
        >
          {effectiveProjectTitle}
        </button>
      );
    }

    return (
      <span className={`min-w-0 truncate font-medium text-gray-900 ${textSizeClass} ${maxWidthClass}`}>
        {effectiveProjectTitle}
      </span>
    );
  }

  function renderProjectReviewStatusBadge(options?: { compact?: boolean }) {
    if (!latestReview) return null;

    let label: string | null = null;
    let className = '';

    switch (latestReview.status) {
      case 'waiting_for_review':
      case 'in_review':
        label = 'In Review';
        className = 'border-amber-200 bg-amber-50 text-amber-700';
        break;
      case 'needs_work':
        label = 'Needs Work';
        className = 'border-rose-200 bg-rose-50 text-rose-700';
        break;
      case 'approved':
        label = 'Approved Assessment';
        className = 'border-green-200 bg-green-50 text-green-700';
        break;
      case 'complete':
        label = 'Complete';
        className = 'border-slate-200 bg-slate-100 text-slate-700';
        break;
      default:
        return null;
    }

    const compact = options?.compact === true;

    return (
      <span
        className={`shrink-0 rounded-full border font-medium ${className} ${
          compact ? 'px-1.5 py-0.5 text-[11px]' : 'px-2 py-0.5 text-xs'
        }`}
      >
        {label}
      </span>
    );
  }

  const handleHistoryClick = useCallback(() => {
    if (!canEdit) return;
    setShowHistory((prev) => !prev);
    setShowComments(false);
  }, [canEdit]);

  const handleCommentsClick = useCallback(() => {
    setShowComments((prev) => !prev);
    setShowHistory(false);
  }, []);

  const handlePreviewRevision = useCallback((revision: Revision) => {
    setPreviewRevision(revision);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewRevision(null);
  }, []);

  const handleRestore = useCallback(async () => {
    if (!projectId || !previewRevision || !canEdit) return;

    await flushSave();

    const [revisionFiles, revisionPdf] = await Promise.all([
      getRevisionFiles(projectId, previewRevision.id),
      getRevisionPdfData(projectId, previewRevision.id).catch(() => null),
    ]);

    await restoreFiles(projectId, revisionFiles.files);
    await refreshFiles();

    if (revisionPdf) {
      replaceResult({
        pdf: revisionPdf,
        log: 'Restored from revision history',
        errors: [],
        diagnostics: [],
      });
      setAutoCompiled(true);
    } else {
      replaceResult(null);
      setAutoCompiled(false);
    }

    if (restoreNoticeTimerRef.current) {
      clearTimeout(restoreNoticeTimerRef.current);
    }
    setRestoreNotice(`Restored revision from ${formatRevisionTimestamp(previewRevision.createdAt)}`);
    restoreNoticeTimerRef.current = setTimeout(() => {
      setRestoreNotice(null);
      restoreNoticeTimerRef.current = null;
    }, 5000);

    setPreviewRevision(null);
    setShowHistory(false);
  }, [projectId, previewRevision, canEdit, flushSave, refreshFiles, replaceResult, formatRevisionTimestamp]);

  const handleAddComment = useCallback((selection: {
    startLine: number;
    endLine: number;
    startColumn: number;
    endColumn: number;
    text: string;
  }) => {
    if (!canComment) return;
    setCommentSubmitError(null);
    setCommentSelection(selection);
    setCommentPromptOpen(true);
  }, [canComment]);

  const handleSelectComment = useCallback((commentId: string) => {
    setSelectedCommentId(commentId);
    setShowComments(true);
    setShowHistory(false);
  }, []);

  const handleSubmitComment = useCallback(async (text: string) => {
    if (!projectId || !activeFile || !currentUser || !commentSelection) return;
    const lines = activeFile.content.split('\n');
    const anchorBefore = lines[commentSelection.startLine - 2] || '';
    const anchorAfter = lines[commentSelection.endLine] || '';
    const snippet = commentSelection.text.replace(/\s+/g, ' ').trim().slice(0, 120);
    setCommentSubmitError(null);
    setSubmittingComment(true);

    try {
      await addComment(projectId, {
        projectId,
        fileId: activeFile.id,
        authorId: currentUser.uid,
        text,
        selection: commentSelection,
        anchorBefore,
        anchorAfter,
        snippet: snippet || commentSelection.text.trim().slice(0, 120),
      });

      setCommentPromptOpen(false);
      setCommentSelection(null);
    } catch (err) {
      const message = getCommentActionErrorMessage(
        err,
        'Failed to add your comment. Please try again.'
      );
      setCommentSubmitError(message);
      throw err;
    } finally {
      setSubmittingComment(false);
    }
  }, [projectId, activeFile, currentUser, commentSelection]);

  const handleSubmitForReview = useCallback(async () => {
    if (!projectId || !project || !currentUser || !reviewEnabled || latestOpenReview || compiling) return;

    setReviewSubmitError(null);
    if (canEdit) {
      await flushSave();
    }

    setSubmittingReview(true);
    try {
      const texFile = getCompileTargetFile(files, activeFile);
      if (!texFile) {
        setReviewSubmitError(MISSING_MAIN_TEX_ERROR);
        return;
      }

      const compilationResult = await compile(texFile.content, files, { writeArtifacts: canEdit });
      if (!compilationResult.pdf) {
        setReviewSubmitError(
          compilationResult.log || 'Compilation failed. Fix the compile errors before submitting for review.'
        );
        return;
      }

      const reviewId = await createReviewRequest({
        projectId,
        ownerId: project.ownerId,
        projectTitle: effectiveProjectTitle,
        submitterId: currentUser.uid,
        submitterName: currentUser.displayName || currentUser.email || 'Unknown',
        submitterEmail: currentUser.email,
        submitterNotes: reviewSubmitNotes,
      });

      const now = new Date();
      setLatestOpenReview({
        id: reviewId,
        projectId,
        ownerId: project.ownerId,
        projectTitle: effectiveProjectTitle,
        status: 'waiting_for_review',
        submitterId: currentUser.uid,
        submitterName: currentUser.displayName || currentUser.email || 'Unknown',
        submitterNotes: reviewSubmitNotes.trim(),
        createdAt: now,
        updatedAt: now,
      });
      setLatestReview({
        id: reviewId,
        projectId,
        ownerId: project.ownerId,
        projectTitle: effectiveProjectTitle,
        status: 'waiting_for_review',
        submitterId: currentUser.uid,
        submitterName: currentUser.displayName || currentUser.email || 'Unknown',
        submitterNotes: reviewSubmitNotes.trim(),
        createdAt: now,
        updatedAt: now,
      });
      setShowReviewSubmitModal(false);
      setShowReviewSubmittedModal(true);
      setReviewSubmitNotes('');
      setReviewSubmitError(null);
    } catch (err) {
      console.error('Failed to submit review request:', err);
      setReviewSubmitError('Failed to submit for review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  }, [
    activeFile,
    canEdit,
    compile,
    compiling,
    currentUser,
    effectiveProjectTitle,
    files,
    projectId,
    project,
    reviewEnabled,
    latestOpenReview,
    flushSave,
    reviewSubmitNotes,
  ]);

  // Auth guard: redirect unauthenticated (non-anonymous) users to sign in
  if (authLoading || userFlags.loading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/auth" replace />;
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <p className="text-red-600 mb-4">{error || 'Project not found'}</p>
        <button
          onClick={handleBackNavigation}
          className="text-primary hover:underline"
        >
          {backButtonTitle}
        </button>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="h-screen flex flex-col bg-editor-bg overflow-hidden">
        {/* Mobile header */}
        <header className="h-12 flex items-center justify-between px-3 border-b border-editor-border bg-white shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={handleBackNavigation}
              className="p-1 rounded hover:bg-gray-100 shrink-0"
              title={backButtonTitle}
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <FileText className="h-4 w-4 text-primary shrink-0" />
            {renderProjectTitleControl({ compact: true })}
            {renderProjectReviewStatusBadge({ compact: true })}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {((isVerifierFile && canRunVerifier) || (!isVerifierFile && canCompile)) && (
              <button
                onClick={isVerifierFile ? (verifier.running ? verifier.stop : () => { void handleRunVerifier(); }) : handleCompile}
                disabled={
                  isVerifierFile
                    ? (!verifier.running && (!canRunVerifier || !activeFile))
                    : (compiling || !engineReady)
                }
                className="flex items-center gap-1 px-2.5 py-1 bg-primary text-white text-xs font-medium rounded hover:bg-primary-hover disabled:opacity-50"
              >
                {isVerifierFile ? (
                  verifier.running ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Play className="h-3.5 w-3.5" />
                  )
                ) : compiling || !engineReady ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Play className="h-3.5 w-3.5" />
                )}
                {isVerifierFile
                  ? verifier.running
                    ? 'Running'
                    : 'Run verifier now'
                  : !engineReady
                  ? 'Loading'
                  : compiling
                  ? 'Compiling'
                  : 'Compile'}
              </button>
            )}
          </div>
        </header>

        {/* Preview / verifier output */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {showVerifierPanel ? (
            <VerifierOutputPanel
              canRun={canRunVerifier}
              entrypoint={verifier.entrypoint || activeFile?.name || null}
              error={verifier.error}
              errorDetails={verifier.errorDetails}
              events={verifier.events}
              onClear={verifier.clear}
              onRun={(model) => { void handleRunVerifier(model); }}
              onStop={verifier.stop}
              phase={verifier.phase}
              running={verifier.running}
              snapshotCapturedAt={verifier.snapshotCapturedAt}
              status={verifier.status}
            />
          ) : (
            <PDFViewer
              pdfData={result?.pdf || null}
              loading={compiling}
              error={result?.errors.length ? result.errors[0] : null}
              onDownload={handleDownload}
            />
          )}
        </div>

        {/* Desktop-only notice */}
        <div className="shrink-0 px-4 py-3 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Monitor className="h-4 w-4 text-gray-400 shrink-0" />
            <span>Editing is only available on desktop. Use a larger screen to edit this project.</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-editor-bg overflow-hidden">
      {/* Header */}
      <header className="h-12 flex items-center justify-between px-4 border-b border-editor-border bg-white">
        <div className="flex min-w-0 items-center gap-3">
          <button
            onClick={handleBackNavigation}
            className="p-1 rounded hover:bg-gray-100"
            title={backButtonTitle}
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <FileText className="h-5 w-5 text-primary" />
          {renderProjectTitleControl()}
          {renderProjectReviewStatusBadge()}
          {canEdit && (
            <button
              onClick={handleHistoryClick}
              title="Revision History"
              className={`p-1 rounded hover:bg-gray-100 ${
                showHistory ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <History className="h-4 w-4" />
            </button>
          )}
          {accessRole && accessRole !== 'owner' && (
            <span
              className={`px-2 py-0.5 text-xs rounded-full border ${
                isAnonymous || accessRole === 'read'
                  ? 'bg-amber-50 text-amber-700 border-amber-200'
                  : accessRole === 'comment'
                  ? 'bg-blue-50 text-blue-700 border-blue-200'
                  : 'bg-green-50 text-green-700 border-green-200'
              }`}
            >
              {isAnonymous
                ? 'View Only'
                : accessRole === 'read'
                ? 'Read Only'
                : accessRole === 'comment'
                ? 'Comments Only'
                : 'Editable'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Presence avatars */}
          {presence.remoteUsers.length > 0 && (
            <div className="relative flex items-center" ref={presencePopupRef}>
              <button
                onClick={() => setShowPresencePopup(!showPresencePopup)}
                className="flex items-center -space-x-1.5 hover:opacity-90 transition-opacity"
                title={`${presence.remoteUsers.length} collaborator${presence.remoteUsers.length > 1 ? 's' : ''} online`}
              >
                {presence.remoteUsers.slice(0, 3).map((user) => (
                  <div
                    key={user.userId}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium text-white border-2 border-white"
                    style={{ backgroundColor: user.color }}
                  >
                    {getInitials(user.displayName)}
                  </div>
                ))}
                {presence.remoteUsers.length > 3 && (
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-medium text-gray-600 bg-gray-100 border-2 border-white">
                    +{presence.remoteUsers.length - 3}
                  </div>
                )}
              </button>

              {/* Presence popup */}
              {showPresencePopup && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100 mb-1">
                    {presence.remoteUsers.length} collaborator{presence.remoteUsers.length > 1 ? 's' : ''} online
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {presence.remoteUsers.map((user) => {
                      const userFile = files.find(f => f.id === user.activeFileId);
                      const isInSameFile = user.activeFileId === activeFile?.id;
                      return (
                        <div key={user.userId} className="px-3 py-2 flex items-center gap-2.5 hover:bg-gray-50">
                          <div className="relative shrink-0">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white"
                              style={{ backgroundColor: user.color }}
                            >
                              {getInitials(user.displayName)}
                            </div>
                            <div
                              className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white"
                              style={{ backgroundColor: user.online ? '#22c55e' : '#9ca3af' }}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {user.displayName}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {userFile
                                ? isInSameFile
                                  ? `In ${userFile.name}`
                                  : `Editing ${userFile.name}`
                                : 'Viewing project'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex items-center gap-1">
            {accessRole === 'owner' && (
              <button
                onClick={() => setShowShareDialog(true)}
                className="inline-flex items-center gap-1 px-2 py-0.5 border border-gray-300 rounded-md text-xs text-gray-700 hover:bg-gray-50"
              >
                <Share2 className="h-3.5 w-3.5" />
                Share
              </button>
            )}
            {canEdit && reviewEnabled && (
              <button
                onClick={() => {
                  setReviewSubmitError(null);
                  setShowReviewSubmitModal(true);
                }}
                disabled={submittingReview || compiling || !!latestOpenReview}
                className="inline-flex items-center gap-1 px-2 py-0.5 border border-gray-300 rounded-md text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-3.5 w-3.5" />
                {latestOpenReview ? 'Under Review' : 'Submit for Review'}
              </button>
            )}
            {canEdit && (
              <button
                onClick={handleSave}
                disabled={effectiveSaveStatus === 'saving'}
                className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded-md text-xs hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed ${
                  effectiveSaveStatus === 'error' ? 'text-red-600 border-red-300' : effectiveSaveStatus === 'saved' ? 'text-green-600 border-green-300' : 'text-gray-700 border-gray-300'
                }`}
              >
                {effectiveSaveStatus === 'saving' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : effectiveSaveStatus === 'saved' ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                {effectiveSaveStatus === 'saving' ? 'Saving...' : effectiveSaveStatus === 'saved' ? 'Saved' : effectiveSaveStatus === 'error' ? 'Save failed' : 'Save'}
              </button>
            )}
          </div>
          {effectiveLastSavedAt && canEdit && (
            <span className="text-xs text-gray-400">
              {effectiveLastSavedAt.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
          {isAnonymous ? (
            <a
              href="/auth"
              className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
            >
              <LogIn className="h-3.5 w-3.5" />
              Sign in
            </a>
          ) : (
            <span className="text-sm text-gray-500">
              {currentUser?.displayName || currentUser?.email}
            </span>
          )}
        </div>
      </header>

      {/* OT fallback banner */}
      {isOTEnabled && otSync.syncStatus === 'fallback' && (
        <div className="px-4 py-1.5 bg-orange-50 border-b border-orange-200 text-xs text-orange-700 flex items-center gap-2">
          <WifiOff className="h-3.5 w-3.5" />
          Real-time sync unavailable — changes are being saved locally
        </div>
      )}

      {restoreNotice && (
        <div className="px-4 py-2 bg-green-50 border-b border-green-200 text-sm text-green-800 flex items-center gap-2">
          <Check className="h-4 w-4 flex-shrink-0" />
          {restoreNotice}
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <div className="w-64 flex flex-col border-r border-editor-border bg-editor-sidebar">
          <div className="grid grid-cols-2 gap-1 p-2 border-b border-editor-border bg-white">
            <button
              onClick={() => setSidebarTab('files')}
              className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                sidebarTab === 'files'
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <FolderTree className="h-4 w-4" />
              Files
            </button>
            <button
              onClick={() => setSidebarTab('chats')}
              className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                sidebarTab === 'chats'
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <MessageSquare className="h-4 w-4" />
              Chats
            </button>
          </div>

          <div className="flex-1 overflow-auto">
            {sidebarTab === 'files' ? (
              <FileTree
                files={files}
                activeFileId={activeFile?.id}
                onFileSelect={(file) => switchToFile(file)}
                projectId={projectId!}
                projectTitle={effectiveProjectTitle}
                onDownloadZip={handleDownloadProjectZip}
                onFilesChange={setFiles}
                canEdit={canEdit}
              />
            ) : (
              <ChatList
                chats={chats}
                activeChatId={activeChatId}
                loading={chatsLoading}
                canEdit={canEdit}
                onDeleteChat={handleDeleteChat}
                onSelectChat={handleSelectChat}
                onNewChat={handleNewChat}
                deletingChatId={deletingChatId}
              />
            )}
          </div>
        </div>

        <div ref={editorWorkspaceRef} className="flex flex-1 min-w-0 overflow-hidden">
          {/* Editor panel */}
          <div className="flex-1 flex flex-col min-w-0 min-h-0">
            {/* Compile bar */}
            <div className="flex min-h-[52px] items-center justify-end px-3 py-2 bg-editor-panel border-b border-editor-border">
              {((isVerifierFile && canRunVerifier) || (!isVerifierFile && canCompile)) && (
                <button
                  onClick={isVerifierFile ? (verifier.running ? verifier.stop : () => { void handleRunVerifier(); }) : handleCompile}
                  disabled={
                    isVerifierFile
                      ? (!verifier.running && (!activeFile))
                      : (compiling || !engineReady || !canCompile)
                  }
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-sm font-medium rounded hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isVerifierFile ? (
                    verifier.running ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )
                  ) : compiling || !engineReady ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  {isVerifierFile
                    ? verifier.running
                      ? 'Running verifier...'
                      : 'Run verifier now'
                    : !engineReady
                    ? 'Loading...'
                    : compiling
                    ? 'Compiling...'
                    : 'Compile'}
                </button>
              )}
            </div>
            <div className="flex-1 min-h-0 overflow-hidden relative">
              {sidebarTab === 'chats' ? (
                <ChatPanel
                  title={activeChat?.title || null}
                  subtitle="General chat by default. Add /context to include the current file and related project files."
                  conversationKey={activeConversationKey}
                  messages={chatMessages}
                  onSubmit={handleChatSubmit}
                  loading={chatLoading}
                  disabled={chatLoading || aiAssistance.hasPendingReview || !canEdit || !activeFile || activeFile.type === 'image'}
                  error={chatError}
                  onDismissError={() => setChatError(null)}
                  draftValue={activeChatDraft}
                  onDraftChange={handleChatDraftChange}
                  initialScrollTop={activeChatScrollTop}
                  onScrollPositionChange={handleChatScrollPositionChange}
                  emptyStateDescription="Start a general AI chat from the sidebar without loading project context."
                  emptyStateHint="Type /context before your prompt if you want the AI to inspect the current file and related project files."
                  loadingMessage="Thinking through your question..."
                  inputPlaceholder="Ask AI anything, or type /context to include this project..."
                />
              ) : activeFile ? (
                activeFile.type === 'image' ? (
                  <ImagePreview src={activeFile.content} fileName={activeFile.name} />
                ) : isStorageBackedFile(activeFile) ? (
                  <LargeFilePlaceholder
                    file={activeFile}
                    projectId={projectId!}
                    canEdit={canEdit}
                    onFileUpdated={(updated) => {
                      setFiles((current) =>
                        current.map((f) => (f.id === updated.id ? updated : f))
                      );
                    }}
                  />
                ) : (
                  <>
                    <LatexEditor
                      fileId={activeFile.id}
                      fileName={activeFile.name}
                      value={activeFile.content}
                      onChange={handleContentChange}
                      onSave={handleSave}
                      onCompile={isVerifierFile && canRunVerifier ? () => { void handleRunVerifier(); } : handleCompile}
                      readOnly={!canEdit}
                      spellcheckEnabled={spellcheckEnabled}
                      canComment={canComment}
                      onAddComment={handleAddComment}
                      onSelectComment={handleSelectComment}
                      selectedCommentId={selectedCommentId}
                      commentRanges={comments.map((comment) => ({
                        id: comment.id,
                        startLine: comment.selection.startLine,
                        endLine: comment.selection.endLine,
                        startColumn: comment.selection.startColumn,
                        endColumn: comment.selection.endColumn,
                      }))}
                      compileDiagnostics={activeFileCompileDiagnostics}
                      allFiles={files}
                      otEnabled={isOTEnabled}
                      onOTChange={handleOTChange}
                      remoteOps={pendingRemoteOps}
                      remoteUsers={remoteUsersInFile}
                      onCursorChange={presence.updateCursor}
                      onSelectionChange={presence.updateSelection}
                      onRemoteContentSync={handleRemoteContentSync}
                      viewState={editorViewStatesRef.current[activeFile.id] || null}
                      onViewStateChange={handleEditorViewStateChange}
                    />
                    <FloatingChatInput
                      onSubmit={handleAISubmit}
                      loading={aiAssistance.loading}
                      disabled={aiAssistance.hasPendingReview || !canEdit}
                      error={aiAssistance.error}
                      onDismissError={aiAssistance.clearError}
                    />
                  </>
                )
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  Select a file to edit
                </div>
              )}
            </div>
          </div>

          <div
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize preview panel"
            onMouseDown={handlePdfPanelResizeStart}
            onDoubleClick={handlePdfPanelResizeReset}
            className={`group relative shrink-0 cursor-col-resize bg-editor-bg transition-colors ${
              isResizingPdfPanel ? 'bg-blue-100' : 'hover:bg-blue-50'
            }`}
            style={{ width: `${PANEL_RESIZER_WIDTH}px` }}
          >
            <div className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-editor-border transition-colors group-hover:bg-blue-300" />
          </div>

          {/* PDF Preview panel / History panel / AI Diff Review panel */}
          <div
            className="w-[40%] min-w-[280px] shrink-0 border-l border-editor-border"
            style={pdfPanelWidth ? { width: `${pdfPanelWidth}px` } : undefined}
          >
            {canRunVerifier && verifier.running ? (
              <VerifierOutputPanel
                canRun={canRunVerifier}
                entrypoint={verifier.entrypoint || activeFile?.name || null}
                error={verifier.error}
                errorDetails={verifier.errorDetails}
                events={verifier.events}
                onClear={verifier.clear}
                onRun={(model) => { void handleRunVerifier(model); }}
                onStop={verifier.stop}
                phase={verifier.phase}
                running={verifier.running}
                snapshotCapturedAt={verifier.snapshotCapturedAt}
                status={verifier.status}
              />
            ) : aiAssistance.hasPendingReview ? (
              <DiffReviewPanel
                chunks={aiAssistance.chunks}
                summary={aiAssistance.summary}
                error={aiAssistance.error}
                onAcceptChunk={aiAssistance.acceptChunk}
                onRejectChunk={aiAssistance.rejectChunk}
                onCancel={aiAssistance.cancel}
                applyingChunkId={aiAssistance.applyingChunkId}
              />
            ) : showComments ? (
              <CommentsPanel
                projectId={projectId!}
                comments={comments}
                loading={commentsLoading}
                canReply={canComment}
                selectedCommentId={selectedCommentId}
                onSelectComment={handleSelectComment}
                canResolve={canComment}
                onClose={() => setShowComments(false)}
              />
            ) : showHistory ? (
              <RevisionList
                projectId={projectId!}
                onClose={() => setShowHistory(false)}
                onPreview={handlePreviewRevision}
              />
            ) : showVerifierPanel ? (
              <VerifierOutputPanel
                canRun={canRunVerifier}
                entrypoint={verifier.entrypoint || activeFile?.name || null}
                error={verifier.error}
                errorDetails={verifier.errorDetails}
                events={verifier.events}
                onClear={verifier.clear}
                onRun={(model) => { void handleRunVerifier(model); }}
                onStop={verifier.stop}
                phase={verifier.phase}
                running={verifier.running}
                snapshotCapturedAt={verifier.snapshotCapturedAt}
                status={verifier.status}
              />
            ) : (
              <PDFViewer
                pdfData={result?.pdf || null}
                loading={compiling}
                error={result?.errors.length ? result.errors[0] : null}
                onDownload={handleDownload}
                onCommentsClick={handleCommentsClick}
                commentsActive={showComments}
                commentCount={comments.length}
              />
            )}
          </div>
        </div>
      </div>

      {/* Revision Preview Modal */}
      {previewRevision && (
        <RevisionPreview
          projectId={projectId!}
          revision={previewRevision}
          onClose={handleClosePreview}
          onRestore={handleRestore}
        />
      )}

      {showReviewSubmitModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Submit for Review</h2>
            <p className="text-sm text-gray-600 mb-4">
              Send this project to the admin review queue. We&apos;ll save your changes and compile the latest PDF before submitting. Notes are optional.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={reviewSubmitNotes}
              onChange={(event) => setReviewSubmitNotes(event.target.value)}
              placeholder="Anything the reviewer should know."
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
            {reviewSubmitError && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 whitespace-pre-wrap">
                {reviewSubmitError}
              </div>
            )}
            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                onClick={() => {
                  setShowReviewSubmitModal(false);
                  setReviewSubmitNotes('');
                  setReviewSubmitError(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitForReview}
                disabled={submittingReview || compiling}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {submittingReview && <Loader2 className="h-4 w-4 animate-spin" />}
                {submittingReview ? 'Saving and Compiling...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReviewSubmittedModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Thanks for your submission</h2>
            <p className="text-sm text-gray-600">
              Your files were saved and the latest PDF was compiled successfully.
            </p>
            <p className="mt-3 text-sm text-gray-600">
              You should expect an email in the next few days letting you know whether your project was approved or needs revisions.
            </p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setShowReviewSubmittedModal(false)}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Selection Prompt Modal */}
      <SelectionPrompt
        isOpen={selectionPromptOpen}
        onSubmit={handleSelectionSubmit}
        onClose={() => {
          setSelectionPromptOpen(false);
          setCurrentSelection(null);
        }}
      />

      <CommentPrompt
        isOpen={commentPromptOpen}
        onSubmit={handleSubmitComment}
        error={commentSubmitError}
        submitting={submittingComment}
        onClose={() => {
          setCommentPromptOpen(false);
          setCommentSubmitError(null);
          setCommentSelection(null);
        }}
      />

      {projectId && currentUser && (
        <ShareDialog
          isOpen={showShareDialog}
          projectId={projectId}
          ownerId={currentUser.uid}
          projectTitle={effectiveProjectTitle}
          projectUpdatedAt={project?.updatedAt || new Date()}
          onClose={() => setShowShareDialog(false)}
        />
      )}
    </div>
  );
}
